
/**
 * ScopingStylesProcessor - Creates scoping styles for shadow DOM
 */
export class ScopingStylesProcessor {
  /**
   * Creates a scoping style element to ensure styles are properly contained
   * within the shadow DOM
   */
  static createScopingStyles(): HTMLStyleElement {
    console.log(`[DEBUG] ScopingStylesProcessor: Creating scoping styles`);
    const style = document.createElement('style');
    style.textContent = `
      /* Root scoping styles */
      :host {
        display: block !important;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
        color: inherit !important;
        line-height: 1.5 !important;
        width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
        --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
        --shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
        --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
        --primary-color: #FB923C;
        --primary-hover: #f97316;
        position: relative !important;
        font-size: 16px !important;
        text-size-adjust: 100% !important;
        -webkit-font-smoothing: antialiased !important;
        margin: 0 !important;
        padding: 0 !important;
      }

      /* Global element styles */
      :host * {
        box-sizing: border-box !important;
        max-width: 100% !important;
        font-family: inherit !important;
        line-height: inherit !important;
      }
      
      /* Container styles */
      .onexip-calculator-container {
        display: block !important;
        width: 100% !important;
        min-height: 50px !important;
        background-color: white !important;
        border-radius: 0.5rem !important;
        box-shadow: var(--shadow-md) !important;
        overflow: hidden !important;
        margin: 0 !important;
        padding: 0 !important;
      }

      .onexip-calculator-wrapper {
        display: block !important;
        width: 100% !important;
        background-color: white !important;
      }
      
      /* Reset styles for common elements */
      .onexip-calculator-container h1,
      .onexip-calculator-container h2,
      .onexip-calculator-container h3,
      .onexip-calculator-container h4,
      .onexip-calculator-container h5,
      .onexip-calculator-container h6,
      .onexip-calculator-container p,
      .onexip-calculator-container div,
      .onexip-calculator-container span,
      .onexip-calculator-container input,
      .onexip-calculator-container select,
      .onexip-calculator-container button {
        margin: 0 !important;
        padding: 0 !important;
        font-family: inherit !important;
        line-height: inherit !important;
      }

      /* Button styles - ensuring visibility and proper styling */
      .onexip-calculator-container button,
      .onexip-calculator-container [role="button"] {
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 0.5rem !important;
        padding: 0.5rem 1rem !important;
        border-radius: 0.375rem !important;
        font-weight: 500 !important;
        font-size: 0.875rem !important;
        line-height: 1.5 !important;
        cursor: pointer !important;
        transition: all 0.2s ease !important;
        background-color: var(--primary-color) !important;
        color: white !important;
        border: none !important;
        outline: none !important;
        min-height: 2.5rem !important;
        opacity: 1 !important;
        position: relative !important;
        z-index: 10 !important;
      }
      
      .onexip-calculator-container button:hover:not(:disabled),
      .onexip-calculator-container [role="button"]:hover:not(:disabled) {
        background-color: var(--primary-hover) !important;
        opacity: 1 !important;
      }
      
      .onexip-calculator-container button:focus-visible {
        outline: 2px solid var(--primary-color) !important;
        outline-offset: 2px !important;
      }
      
      .onexip-calculator-container button:disabled {
        opacity: 0.5 !important;
        cursor: not-allowed !important;
      }
      
      .onexip-calculator-container button.variant-outline {
        background-color: transparent !important;
        color: var(--primary-color) !important;
        border: 1px solid #d4d4d8 !important;
      }
      
      .onexip-calculator-container button.variant-outline:hover:not(:disabled) {
        background-color: rgba(251, 146, 60, 0.1) !important;
      }

      /* Step indicator styles */
      .onexip-calculator-container .flex {
        display: flex !important;
        flex-direction: row !important;
      }
      
      .onexip-calculator-container .flex-col {
        flex-direction: column !important;
      }
      
      .onexip-calculator-container .justify-between {
        justify-content: space-between !important;
      }
      
      .onexip-calculator-container .step-item {
        position: relative !important;
        z-index: 2 !important;
      }

      .onexip-calculator-container .step-item:not(:first-child):before {
        content: "" !important;
        position: absolute !important;
        height: 3px !important;
        width: 100% !important;
        background-color: #e2e8f0 !important;
        left: -50% !important;
        top: 1.25rem !important;
        transform: translateY(-50%) !important;
        z-index: 1 !important;
      }
      
      .onexip-calculator-container .step-item.active:not(:first-child):before,
      .onexip-calculator-container .step-item.complete:not(:first-child):before {
        background-color: var(--primary-color) !important;
      }
      
      /* Platform selector grid */
      .onexip-calculator-container .grid {
        display: grid !important;
      }
      
      .onexip-calculator-container .grid-cols-1 {
        grid-template-columns: repeat(1, minmax(0, 1fr)) !important;
      }
      
      @media (min-width: 768px) {
        .onexip-calculator-container .md\\:grid-cols-2 {
          grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        }
      }
      
      /* Spacing utilities */
      .onexip-calculator-container .gap-4 {
        gap: 1rem !important;
      }
      
      .onexip-calculator-container .mb-6 {
        margin-bottom: 1.5rem !important;
      }
      
      .onexip-calculator-container .mb-4 {
        margin-bottom: 1rem !important;
      }
      
      .onexip-calculator-container .mt-2 {
        margin-top: 0.5rem !important;
      }
      
      /* Text alignment */
      .onexip-calculator-container .text-center {
        text-align: center !important;
      }
      
      .onexip-calculator-container .text-left {
        text-align: left !important;
      }
      
      /* Card and border styles */
      .onexip-calculator-container .border {
        border-width: 1px !important;
        border-style: solid !important;
        border-color: #e2e8f0 !important;
      }
      
      .onexip-calculator-container .border-primary {
        border-color: var(--primary-color) !important;
      }
      
      .onexip-calculator-container .rounded-lg {
        border-radius: 0.5rem !important;
      }
      
      .onexip-calculator-container .p-6 {
        padding: 1.5rem !important;
      }
      
      .onexip-calculator-container .cursor-pointer {
        cursor: pointer !important;
      }
      
      .onexip-calculator-container .bg-primary\\/10 {
        background-color: rgba(251, 146, 60, 0.1) !important;
      }

      /* Icon styles */
      .onexip-calculator-container svg {
        display: inline-block !important;
        height: 1em !important;
        width: 1em !important;
        vertical-align: middle !important;
      }
    `;
    
    console.log(`[DEBUG] Created scoping styles element with ${style.textContent.length} characters`);
    return style;
  }
}
